import { Checkbox, Stack, SxProps, Theme, Typography } from '@mui/material';
import { checkboxIconProps } from 'Components/CheckboxIcons';
import { TypographyVariants } from 'ThemeV2.0';
import { getCheckboxLabelTypography } from 'Utils/checkboxLabelTypography';
import { stopPropagationAccessibilityBehavior } from 'Utils/accessibilityOnKeyDown';

interface ICheckboxInput {
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label: string;
  textVariant?: TypographyVariants;
  textSx?: SxProps;
  containerStyle?: SxProps<Theme>;
  isError?: boolean;
  errorMsg?: string;
  isRequired?: boolean;
  ariaLabel?: string;
}

export const CheckboxInput = ({
  checked,
  label,
  onChange,
  textVariant,
  textSx,
  containerStyle,
  isError,
  errorMsg,
  isRequired,
  ariaLabel,
}: ICheckboxInput) => {
  const labelTypography = getCheckboxLabelTypography(checked);
  const { icon, checkedIcon } = checkboxIconProps(isError);

  return (
    <Stack
      sx={{
        alignItems: 'center',
        flexDirection: 'row',
        gap: '8px',
        ...containerStyle,
      }}
      onKeyDown={stopPropagationAccessibilityBehavior}
    >
      <Stack
        direction='row'
        sx={{
          gap: '4px',
        }}
      >
        <Checkbox
          sx={{
            pr: '9px',
          }}
          tabIndex={0}
          checked={checked}
          onChange={onChange}
          inputProps={{
            'aria-label': `${(ariaLabel || label).trim().replace(' ', '-')}`,
            'aria-describedby':
              isError && errorMsg
                ? `${label.trim().replace(' ', '-')}-error`
                : undefined,
            'aria-required': isRequired,
            'aria-invalid': isError,
          }}
          icon={icon}
          checkedIcon={checkedIcon}
        />
        <Stack
          sx={
            isError
              ? { position: 'relative', top: '10px' }
              : { justifyContent: 'center' }
          }
        >
          <Typography
            variant={textVariant || labelTypography.variant}
            sx={{
              width: 'fit-content',
              ...labelTypography.sx,
              ...textSx,
            }}
          >
            {label}
          </Typography>
          {isError && errorMsg && (
            <Typography
              id={`${label.trim().replaceAll(' ', '-')}-error`}
              variant='body6'
              sx={{ color: '#E12D2D' }}
            >
              {errorMsg}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
